import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { Textarea } from '../components/common/Textarea';
import Modal from '../components/common/Modal';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface LeaveType {
  id: number;
  name: string;
  description: string;
  color: string;
  requires_approval: boolean;
  is_paid: boolean;
  is_public_holiday: boolean;
  default_days: number | null;
  icon: string | null;
  created_at: string;
  updated_at: string;
}

interface LeaveTypeFormData {
  name: string;
  description: string;
  color: string;
  requires_approval: boolean;
  is_paid: boolean;
  is_public_holiday: boolean;
  default_days: number | null;
  icon: string | null;
}

const LeaveTypeManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveTypeToDelete, setLeaveTypeToDelete] = useState<LeaveType | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue
  } = useForm<LeaveTypeFormData>();

  const { data: leaveTypes, isLoading } = useQuery({
    queryKey: ['leave-types'],
    queryFn: async () => {
      const response = await api.get('/leave-types');
      return response.data;
    }
  });

  const createLeaveTypeMutation = useMutation({
    mutationFn: async (data: LeaveTypeFormData) => {
      const response = await api.post('/leave-types', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-types'] });
      toast.success(t('leaveTypes.createSuccess', 'Leave type created successfully'));
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('leaveTypes.createError', 'Failed to create leave type'));
    }
  });

  const updateLeaveTypeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: LeaveTypeFormData }) => {
      const response = await api.put(`/leave-types/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-types'] });
      toast.success(t('leaveTypes.updateSuccess', 'Leave type updated successfully'));
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('leaveTypes.updateError', 'Failed to update leave type'));
    }
  });

  const deleteLeaveTypeMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/leave-types/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-types'] });
      toast.success(t('leaveTypes.deleteSuccess', 'Leave type deleted successfully'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('leaveTypes.deleteError', 'Failed to delete leave type'));
    }
  });

  const openModal = (leaveType?: LeaveType) => {
    if (leaveType) {
      setSelectedLeaveType(leaveType);
      reset({
        name: leaveType.name,
        description: leaveType.description,
        color: leaveType.color,
        requires_approval: leaveType.requires_approval,
        is_paid: leaveType.is_paid,
        is_public_holiday: leaveType.is_public_holiday,
        default_days: leaveType.default_days,
        icon: leaveType.icon
      });
    } else {
      setSelectedLeaveType(null);
      reset({
        name: '',
        description: '',
        color: '#3788d8',
        requires_approval: true,
        is_paid: true,
        is_public_holiday: false,
        default_days: null,
        icon: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLeaveType(null);
    reset();
  };

  const onSubmit = (data: LeaveTypeFormData) => {
    if (selectedLeaveType) {
      updateLeaveTypeMutation.mutate({ id: selectedLeaveType.id, data });
    } else {
      createLeaveTypeMutation.mutate(data);
    }
  };

  const handleDelete = (leaveType: LeaveType) => {
    setLeaveTypeToDelete(leaveType);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (leaveTypeToDelete) {
      deleteLeaveTypeMutation.mutate(leaveTypeToDelete.id);
      setDeleteConfirmOpen(false);
      setLeaveTypeToDelete(null);
    }
  };

  const watchColor = watch('color');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.loading', 'Ładowanie...')}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('leaveTypes.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('leaveTypes.subtitle', 'Manage leave types and their properties')}
          </p>
        </div>
        <Button onClick={() => openModal()}>
          <Plus className="h-4 w-4 mr-2" />
          {t('leaveTypes.create')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.name', 'Nazwa')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('leaveTypes.color', 'Color')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('leaveTypes.defaultDays', 'Default Days')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('leaveTypes.properties', 'Properties')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions.title', 'Akcje')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaveTypes?.map((leaveType: LeaveType) => (
                  <tr key={leaveType.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {leaveType.name}
                        </div>
                        {leaveType.description && (
                          <div className="text-sm text-gray-500">
                            {leaveType.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="h-6 w-6 rounded-md mr-2"
                          style={{ backgroundColor: leaveType.color }}
                        />
                        <span className="text-sm text-gray-600">
                          {leaveType.color}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
{leaveType.default_days || t('common.placeholders.na', 'N/A')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        {leaveType.requires_approval && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            {t('leaveTypes.requiresApproval', 'Requires Approval')}
                          </span>
                        )}
                        {leaveType.is_paid && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {t('leaveTypes.paidLeave', 'Paid Leave')}
                          </span>
                        )}
                        {leaveType.is_public_holiday && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {t('leaveTypes.publicHoliday', 'Public Holiday')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openModal(leaveType)}
                        className="mr-2"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(leaveType)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal}
        title={selectedLeaveType ? t('leaveTypes.edit') : t('leaveTypes.create')}
      >
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label={t('common.name', 'Nazwa')}
              {...register('name', { required: t('common.validation.required', 'To pole jest wymagane') })}
              error={errors.name?.message}
            />
            
            <Textarea
              label={t('common.description', 'Opis')}
              {...register('description')}
              rows={3}
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('leaveTypes.color', 'Color')}
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  {...register('color', { 
                    required: t('leaveTypes.colorRequired', 'Color is required'),
                    pattern: {
                      value: /^#[0-9A-F]{6}$/i,
                      message: t('leaveTypes.invalidColor', 'Invalid color format')
                    }
                  })}
                  className="h-10 w-10 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  {...register('color')}
                  placeholder="#3788d8"
                  className="flex-1"
                  error={errors.color?.message}
                />
              </div>
            </div>
            
            <Input
              label={t('leaveTypes.defaultDays', 'Default Days')}
              type="number"
              {...register('default_days', { 
                setValueAs: v => v === '' ? null : parseInt(v) 
              })}
              error={errors.default_days?.message}
            />
            
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('requires_approval')}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{t('leaveTypes.requiresApproval', 'Requires Approval')}</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('is_paid')}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{t('leaveTypes.paidLeave', 'Paid Leave')}</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('is_public_holiday')}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{t('leaveTypes.publicHoliday', 'Public Holiday')}</span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="submit"
                disabled={createLeaveTypeMutation.isPending || updateLeaveTypeMutation.isPending}
              >
{selectedLeaveType ? t('common.actions.edit', 'Edytuj') : t('common.actions.create', 'Utwórz')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={closeModal}
              >
{t('common.actions.cancel', 'Anuluj')}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal 
        isOpen={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        title={t('leaveTypes.delete')}
      >
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">
            {t('leaveTypes.deleteConfirm', 'Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: leaveTypeToDelete?.name })}
          </p>
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleteLeaveTypeMutation.isPending}
            >
{t('common.actions.delete', 'Usuń')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
{t('common.actions.cancel', 'Anuluj')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LeaveTypeManagement;